import { useEffect, useMemo, type ReactNode } from 'react';
import { useForm, type DefaultValues, type FieldValues, type UseFormReturn } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { RotateCcw, Save } from 'lucide-react';
import { Button, ErrorState, FormActions, Loading, Panel, useToast } from '@/components';
import { api, ApiError, errorMessage } from '@/lib/api';
import { masterKeys, useCodeValues, useSaveMaster } from '../api';

export interface MasterEditorProps<V extends FieldValues, R> {
  title: string;
  description?: ReactNode;
  /** GET / PUT のパス（例 `/masters/years/2026/wages`） */
  path: string;
  /** 取得結果 → フォーム値 */
  toForm: (data: R) => V;
  /** フォーム値 → PUT の body */
  toBody: (values: V) => unknown;
  /** フォームの描画 */
  children: (form: UseFormReturn<V>, data: R) => ReactNode;
  /** 保存成功時の通知文言 */
  savedMessage?: string;
  /** 追加のクエリ（フォーム値の再構築に使う関連データ）が揃うまで待つ */
  ready?: boolean;
  /** API のエラー path → フォームのフィールド名の変換（body とフォームの構造が違う場合） */
  mapErrorPath?: (path: string) => string;
}

/**
 * 「読み込み → 行を編集 → 一括保存」型マスタの共通枠。
 * API の 400（details: [{path, message}]）は RHF のフィールドエラーへ反映する（行番号つき）。
 */
export function MasterEditor<V extends FieldValues, R>({ title, description, path, toForm, toBody, children, savedMessage = '保存しました', ready = true, mapErrorPath }: MasterEditorProps<V, R>) {
  const toast = useToast();
  const query = useQuery({ queryKey: [...masterKeys.all, 'editor', path], queryFn: ({ signal }) => api.get<R>(path, undefined, { signal }), staleTime: 0, gcTime: 0 });
  const save = useSaveMaster<unknown>(path, 'put');
  const codes = useCodeValues();
  const values = useMemo(() => (query.data && ready ? toForm(query.data) : undefined), [query.data, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  if (query.isPending || !ready || codes.isPending) return <Loading block />;
  if (query.error || !values) return <ErrorState error={query.error ?? new ApiError(500, 'EMPTY', '読み込めませんでした')} onRetry={() => void query.refetch()} />;
  return (
    <EditorBody
      key={`${path}`}
      title={title}
      description={description}
      values={values}
      data={query.data as R}
      toBody={toBody}
      mapErrorPath={mapErrorPath}
      onSave={async (body) => {
        await save.mutateAsync(body);
        await query.refetch();
        toast.success(savedMessage);
      }}
      saving={save.isPending}
    >
      {children}
    </EditorBody>
  );
}

function EditorBody<V extends FieldValues, R>({
  title,
  description,
  values,
  data,
  toBody,
  mapErrorPath,
  onSave,
  saving,
  children,
}: {
  title: string;
  description?: ReactNode;
  values: V;
  data: R;
  toBody: (v: V) => unknown;
  mapErrorPath?: (path: string) => string;
  onSave: (body: unknown) => Promise<void>;
  saving: boolean;
  children: (form: UseFormReturn<V>, data: R) => ReactNode;
}) {
  const toast = useToast();
  const form = useForm<V>({ defaultValues: values as DefaultValues<V>, values });
  const {
    handleSubmit,
    reset,
    setError,
    formState: { isDirty, isSubmitting },
  } = form;

  // 保存後に再取得した値でフォームを初期化する
  useEffect(() => {
    reset(values);
  }, [values, reset]);

  const submit = handleSubmit(async (v) => {
    try {
      await onSave(toBody(v));
    } catch (e) {
      if (e instanceof ApiError && Object.keys(e.fieldErrors).length > 0) {
        Object.entries(e.fieldErrors).forEach(([path, message], i) => setError((mapErrorPath ? mapErrorPath(path) : path) as never, { type: 'server', message }, { shouldFocus: i === 0 }));
        toast.error('保存できませんでした', Object.values(e.fieldErrors)[0]);
      } else toast.error('保存できませんでした', errorMessage(e));
    }
  });

  return (
    <form onSubmit={submit} noValidate>
      <Panel title={title} description={description}>
        {children(form, data)}
      </Panel>
      <FormActions>
        <Button icon={<RotateCcw size={16} />} onClick={() => reset(values)} disabled={!isDirty}>
          変更を元に戻す
        </Button>
        <Button type="submit" variant="primary" icon={<Save size={16} />} loading={isSubmitting || saving} disabled={!isDirty}>
          保存する
        </Button>
      </FormActions>
    </form>
  );
}
